import type { ReactNode } from 'react';
import { CHECKOUT_STEPS, type CheckoutStepId } from '@/lib/checkout/types';

const stepNumberOf = (step: CheckoutStepId): number => CHECKOUT_STEPS.indexOf(step) + 1;

interface CheckoutShellProps {
  step: CheckoutStepId;
  /** Rendered above the step heading — resume / error / decline banners. */
  banner?: ReactNode;
  children: ReactNode;
  summary: ReactNode;
  onBackToPlans: () => void;
}

/**
 * Public, WorldVia-branded chrome for checkout: header, progress and the
 * two-column form + order-summary layout (stacked on small screens).
 */
export default function CheckoutShell({
  step,
  banner,
  children,
  summary,
  onBackToPlans,
}: CheckoutShellProps) {
  const stepNumber = stepNumberOf(step);

  return (
    <div className="min-h-screen bg-[#efeaf1] px-3 py-5 text-[#3d1751] sm:px-6 sm:py-8">
      <div className="mx-auto w-full max-w-[1120px] overflow-hidden rounded-2xl bg-white shadow-[0_14px_40px_rgba(61,23,81,0.16)]">
        <header className="flex flex-wrap items-center gap-4 border-b border-[#ece6f0] px-5 py-3 sm:px-6">
          <div className="leading-none">
            <p className="text-[19px] font-extrabold text-[#3d1751]">
              World<span className="font-serif italic font-normal text-[#4b0a5b]">Via</span>
            </p>
            <p className="mt-0.5 text-[6.5px] font-medium uppercase tracking-[0.3em] text-[#7a6a8c]">
              Travel Network
            </p>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={onBackToPlans}
              className="rounded-full text-[13px] font-medium text-[#b82f74] underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-[#b82f74] focus:ring-offset-2"
            >
              Back To Plans
            </button>
            <p className="text-[12.5px] text-[#7a6a8c]">
              Need help?{' '}
              <a href="tel:+18558467821" className="text-[#b82f74] hover:underline">
                (855) 846-7821
              </a>
            </p>
          </div>
        </header>

        <div className="bg-[#fbfafc] px-4 pb-9 pt-6 sm:px-7">
          {banner}

          <div className="mb-2 flex flex-wrap items-baseline gap-3">
            <h1 className="m-0 text-xl font-bold text-[#3d1751] sm:text-[21px]">
              Membership Subscription Checkout
            </h1>
            <p className="text-[11px] font-medium text-[#7a6a8c]">
              Step {stepNumber} of {CHECKOUT_STEPS.length}
            </p>
          </div>
          <ol
            className="mb-6 flex gap-1.5"
            aria-label={`Checkout progress: step ${stepNumber} of ${CHECKOUT_STEPS.length}`}
          >
            {CHECKOUT_STEPS.map((id) => (
              <li
                key={id}
                aria-hidden="true"
                className={`h-[5px] w-[34px] rounded-full ${
                  stepNumberOf(id) <= stepNumber ? 'bg-[#ff7540]' : 'bg-[#e7ddef]'
                }`}
              />
            ))}
          </ol>

          <div className="flex flex-col items-start gap-5 lg:flex-row lg:gap-6">
            <section className="w-full flex-1 rounded-2xl border border-[#ece6f0] bg-white p-5 sm:p-7">
              {children}
            </section>
            {summary}
          </div>
        </div>
      </div>
    </div>
  );
}
